import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output, QueryList, ViewChildren, inject, viewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Advertiser, Campaign, CampaignBanner, CampaignTargetUser, CampaignsAdvertiserAdminApiClient, ProductReward, PunchCardFlow, PunchCardReceiptToolType, PunchCardStep, PunchCardStrategy, RewardData } from 'src/app/api/api-campaigns.service.generated';
import { RewardsProductApiClient } from 'src/app/api/api-rewards.service.generated';
import { AdvertiserCampaignRewardEditComponent } from '../../advertiser-campaign-reward-edit/advertiser-campaign-reward-edit.component';
import { RewardSelectComponent } from '../../_/reward-select/reward-select.component';
import { PunchCardStrategyType } from '../../../../../api/api-campaigns.service.generated';
import { StampSettingComponent } from '../../_/stamp-setting/stamp-setting.component';
import { PunchCardCampaignService } from '../punch-card-campaign-edit.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, lastValueFrom } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingOverlayComponent, PhotoComponent, ButtonComponent } from 'wecheer-common';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CampaignBannerComponent } from '../../_/campaign-banner/campaign-banner.component';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@angular/flex-layout';

@UntilDestroy()
@Component({
  selector: 'app-punch-card-strategy-flow',
  templateUrl: './punch-card-strategy-flow.component.html',
  styleUrls: ['./punch-card-strategy-flow.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatTooltipModule,
    MatSelectModule,
    MatSlideToggleModule,
    FlexLayoutModule,
    StampSettingComponent,
    RewardSelectComponent,
    CampaignBannerComponent,
    PhotoComponent,
    LoadingOverlayComponent,
    ButtonComponent
]
})
export class PunchCardStrategyFlowComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private advertiserApi = inject(CampaignsAdvertiserAdminApiClient);
  private rewardProductsApi = inject(RewardsProductApiClient);
  private punchCardCampaignService = inject(PunchCardCampaignService);

  readonly stampSetting = viewChild<StampSettingComponent>('stampSetting');

  @Input() strategy: PunchCardStrategy;
  @Input() advertiserId: string;
  @Input() campaignId: string;
  @Input() isCloning: boolean = false;
  @Output() allowFlowChange = new EventEmitter<boolean>();
  @Input() set strategyType(value: PunchCardStrategyType | undefined) {
    if (value) {
      this._strategyType = value;
      this.updateStepValidator();
    }
  }

  _strategyType: PunchCardStrategyType = null;
  advertiser: Advertiser;
  campaigns: Campaign[];
  isLoading = false;

  rewardProductsOptions: RewardData[] = [];

  form = new FormGroup({
    // Flow
    allowFlow: new FormControl(null),
    steps: this.fb.array([], []),
    restartLimit: new FormControl('')
  });

  placeHolderCompleteStep: string;
  cachedSteps: PunchCardStep[] = [];

  @ViewChildren(RewardSelectComponent) levelUnlockRewards: QueryList<RewardSelectComponent>;

  levelUnlockRewardValues: RewardData[] = [];

  @Input() isAdding: boolean;
  allowFlow() { return !!this.form.value.allowFlow; }


  ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];
  ALLOWED_EXTENSIONS_BANNER = ['jpg', 'jpeg', 'png', 'gif'];

  receiptToolTypes = PunchCardReceiptToolType;
  get schemes() {
    return this.punchCardCampaignService.schemList || [];
  }

  get captureToolType() {
    return this.punchCardCampaignService.captureToolType;
  }

  get isTargetStaffRole() {
    return this.punchCardCampaignService.targetRole === CampaignTargetUser.Staff;
  }

  get isAllowFlow() {
    return !!this.form.value.allowFlow;
  }

  get isDisableTier() {
    const isStaff = this.punchCardCampaignService.targetRole === CampaignTargetUser.Staff;
    const isOCR = this.punchCardCampaignService.captureToolType === PunchCardReceiptToolType.OCR;
    const isHandshakeQR = this.punchCardCampaignService.captureToolType === PunchCardReceiptToolType.HandshakeQR;

    return (isStaff && isOCR) || isHandshakeQR;
  }


  async ngOnChanges(_: SimpleChanges) {
    if (!this.advertiserId) return;
    this.placeHolderCompleteStep = this._strategyType === 'Stamps' ? 'Stamps to complete' : 'Points to complete';

    if (this.strategy?.flow?.steps) {
      this.cachedSteps = [...this.strategy?.flow?.steps];
      this.bindSteps(this.strategy?.flow?.steps);
    }
  }

  ngAfterViewInit() {

    this.getAdvertiser();
    this.getProductRewards();

    if (this.strategy && this.strategy.flow)
      this.setFormValue(this.strategy.flow);

    this.bindLevelUnlockRewards();
    this.toggleAllowFlow();
  }

  private bindLevelUnlockRewards() {
    this.levelUnlockRewards.changes.subscribe((r) => {
      var changes = r as QueryList<RewardSelectComponent>;
      changes.forEach((v, i) => {
        if (this.levelUnlockRewardValues[i] != null) {
          v.setFormValue(this.levelUnlockRewardValues[i]);
        }
      });
    });
  }

  async ngOnInit() {
    if (!this.advertiserId) return;
    this.punchCardCampaignService.onGetProductFinished
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.getAdvertiser();
        this.getProductRewards();
      });
    this.onFormDataChange();

  }

  compare2Steps(step1: PunchCardStep, step2: PunchCardStep) {
    if (step1.banner && step2.banner) {
      if (Object.entries(step1?.banner).toString() != Object.entries(step2?.banner).toString()) return false;
    }
    if (step1?.schemaId != step2?.schemaId) return false;
    if (step1?.title != step2?.title) return false;
    if (step1?.maxStampCount != step2?.maxStampCount) return false;
    if (step1?.restartLimit != step2?.restartLimit) return false;
    if (step1.completeReward?.productReward && step2.completeReward?.productReward && step1?.completeReward.productReward.productSlug != step2.completeReward?.productReward.productSlug) return false;
    if (!step1.completeReward?.productReward || !step2.completeReward?.productReward) return false;
    return true;
  }

  onFormDataChange() {
    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .pipe(debounceTime(500))
      .subscribe((value) => {
        if (this.punchCardCampaignService.viewMode == 'flow') {

          for (let i = 0; i < value.steps.length; i++) {
            if (!this.compare2Steps(value.steps[i] as any, this.cachedSteps[i] as any)) {
              this.punchCardCampaignService.setStepList(value.steps.map(item => new PunchCardStep(item)), i, this.cachedSteps[i])
              this.punchCardCampaignService.onFlowChange.next({
                index: i,
                step: this.cachedSteps[i]
              });
            }
          }
          this.cachedSteps = [...value.steps as any];
        }
      })
  }

  onSchemeChange(_ev) {
    this.punchCardCampaignService.setStepList(this.getFormSteps());
  }

  async getAdvertiser() {
    this.advertiser = await lastValueFrom(this.advertiserApi.getAdvertiserById(this.advertiserId));
  }

  async getProductRewards() {
    this.rewardProductsOptions = this.punchCardCampaignService.rewardProductsOptions.map((pi: RewardData) =>
      new RewardData({
        productReward: pi
      }));
  }

  async getRewardData(step: PunchCardStep): Promise<RewardData> {
    if (!step || !step.completeReward || !step.completeReward.productReward || !step.completeReward.productReward.productSlug)
      return undefined;

    const slug = step.completeReward.productReward.productSlug;
    const productInfo = await lastValueFrom(this.rewardProductsApi.getProductBySlug(slug));
    const rewardData = productInfo
      ? new RewardData({
        productReward: new ProductReward({
          productSlug: productInfo.slug,
          rewardTitle: productInfo.name,
          rewardDescription: productInfo.description,
          rewardThumbUrl: productInfo.photoUrl,
          rewardPhotoUrl: productInfo.photoUrl
        })
      })
      : undefined;

    return rewardData;
  }

  async setFormValue(flow: PunchCardFlow) {
    if (!flow)
      return;

    this.bindSteps(flow.steps);
    this.form.patchValue({ allowFlow: !!flow });
    this.form.patchValue({ restartLimit: flow.restartLimit as any });
    this.onAllowFlowChange();
  }

  getFormValue(): any {
    const allowFlow = this.allowFlow();
    if (!this.isValid()) {
      return undefined;
    }
    const punchCardFlow = new PunchCardFlow();
    punchCardFlow.steps = this.getFormSteps();
    punchCardFlow.restartLimit = this.form.get('restartLimit').value as any || undefined;
    return allowFlow ? punchCardFlow : this.stampSetting()?.getFormValue();
  }

  getFormSteps(): PunchCardStep[] {
    const rules = this.form.value.steps.map((x: any, index) => {
      const step = new PunchCardStep({
        title: x.title,
        banner: x.banner ? new CampaignBanner({ ...x.banner }) : undefined,      // new banner slider images
        photoBannerUrl: x.photoBannerUrl,
        photoBannerUploadKey: x.photoBannerUploadKey,
        maxStampCount: +x.maxStampCount,
        restartable: +x.restartLimit > 0,
        restartLimit: parseInt(x.restartLimit) || undefined,
        completeReward: x.completeReward
          ? new RewardData({
            productReward: new ProductReward(x.completeReward.productReward)
          })
          : undefined,
        schemaId: x.schemaId
      });

      if (this.captureToolType === this.receiptToolTypes.UniqueSafebox) {
        delete step.schemaId;
      }

      // add client prop to know if the step was added later - so it can be deleted
      (step as IMyEntityState).isNewStep = x.isNewStep;
      step.levelUnlockReward = this.levelUnlockRewards.toArray()[index].getFormValue();

      return step;
    });

    return rules;
  }


  // rules reactive array
  addStep(step?: PunchCardStep): FormGroup {
    const controls = <FormArray>this.form.controls.steps;
    const grp = new FormGroup({
      isNewStep: new FormControl(true),   // +added new client side extra prop to know if the step was added later - so it can be deleted in this edit session
      title: new FormControl(step?.title, Validators.required),
      photoBannerUrl: new FormControl(),
      photoBannerUploadKey: new FormControl(),
      maxStampCount: new FormControl(step?.maxStampCount, [Validators.required, Validators.min(1), this._strategyType === 'Points' ? Validators.max(99999999) : Validators.pattern('([1-9]|[12][0-9]|3[00])')]),  // [0:30]
      completeReward: new FormControl(step?.completeReward, this._strategyType === 'Stamps' ? Validators.required : null),
      restartable: new FormControl(step?.restartable),
      restartLimit: new FormControl({ value: this._strategyType === 'Points' ? 1 : 0, disabled: this._strategyType === 'Points' }),    // repeat x times
      banner: new FormControl(),       // strategyBase banner
      schemaId: new FormControl(step?.schemaId)
    });

    if (this.captureToolType != this.receiptToolTypes.UniqueSafebox) {
      grp.get('schemaId').setValidators([Validators.required]);
      grp.get('schemaId').updateValueAndValidity();
    }

    controls.push(grp);
    this.levelUnlockRewardValues.push(null);
    return grp;
  }

  updateStepValidator() {
    if (!this.form || !this.form.controls ||
      !this.form.controls.steps || !this.form.controls.steps.controls)
      return;

    for (let count = 0; count < this.form.controls.steps.controls.length; count++) {
      this.form.controls.steps.controls[count].get('completeReward').setValidators(this._strategyType == 'Stamps' ? Validators.required : null);
      this.form.controls.steps.controls[count].get('completeReward').updateValueAndValidity();
    }
  }

  removeStep(index: number) {
    const rules = this.getFormSteps();
    rules.splice(index, 1);
    this.levelUnlockRewardValues.splice(index, 1);
    this.bindSteps(rules);
  }

  bindSteps(steps: PunchCardStep[]) {
    if (!steps) return;

    const reactiveControls = <FormArray>this.form.controls.steps;
    reactiveControls.controls.length = 0;
    reactiveControls.patchValue([]);
    this.punchCardCampaignService.setStepList(steps);

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const grp = this.addStep(step);
      if (this._strategyType === 'Points') {
        step.restartLimit = 1;
      }
      grp.patchValue({
        ...step,
        isNewStep: (step as IMyEntityState).isNewStep
      });

      // for backward compatibility - old campaigns have single step banner -> new campaigns have CampaignBanner complext type (stores 5 images)
      // if no baner (means old campaign -> copy single image url)
      if (step.photoBannerUrl && !(step.banner && step.banner.url1))
        grp.patchValue({ banner: new CampaignBanner({ url1: step.photoBannerUrl }) });

      this.levelUnlockRewardValues[i] = step.levelUnlockReward;
    }
  }


  // complete reward
  async onAddRewardProduct(grp: any) {
    const productReward = await AdvertiserCampaignRewardEditComponent.showDialog(this.dialog, { advertiser: this.advertiser });
    if (!productReward) return;

    productReward.uploadKey = productReward.uploadKey;                     // this flag must create the product reward the the campaing is created

    this.rewardProductsOptions = [
      new RewardData({ productReward: productReward }),                    // push at 0 index
      ...this.rewardProductsOptions
    ];

    grp.patchValue({
      completeReward: new RewardData({ productReward: new ProductReward({ ...productReward }) }),
    });
  }
  async onRemoveRewardProduct(grp: any) {
    grp.patchValue({ completeReward: undefined });
  }
  async onRewardSelected(_productReward: ProductReward, _index: number) {
    // Method placeholder for future implementation
  }

  isValid() {
    if (this.stampSetting()?.isValid() == false) {
      return false;
    }

    if (!this.form.valid) {
      this.forceValidatorsToDisplayErrors();
    }

    return this.form.valid;
  }

  async onSubmit() {
  }

  forceValidatorsToDisplayErrors() {
    // force validators to display errors
    this.form.markAllAsTouched()
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key).markAsTouched();
      this.form.get(key).markAsDirty();
    });
  }

  onAllowFlowChange() {
    this.toggleAllowFlow();
    this.allowFlowChange.emit(this.allowFlow());
  }

  toggleAllowFlow() {
    if (this.isDisableTier) {
      this.form.get('allowFlow').disable();
    } else {
      this.form.get('allowFlow').enable();
    }
  }

  onRoleChange() {
    if (this.isTargetStaffRole && this.captureToolType === PunchCardReceiptToolType.OCR) {
      this.form.patchValue({ allowFlow: false });
      (this.form.get('steps') as FormArray).clear();
      this.onAllowFlowChange();
    }
  }

  compareRewardData: ((a: RewardData, b: RewardData) => boolean) | null = (a: RewardData, b: RewardData) => a && b && a.productReward.productSlug === b.productReward.productSlug;

  hasProductReward(grp: any) {
    return !!(grp.value.completeReward && grp.value.completeReward.productReward);
  }

  forceRefresh() {
    if (this.strategy?.flow?.steps) {
      this.cachedSteps = [...this.strategy.flow.steps];
      this.bindSteps(this.strategy.flow.steps);
    }

    if (this.stampSetting()) {
      this.stampSetting().forceRefresh();
    }
  }
}


export interface IMyEntityState {
  isNewStep?: boolean;
}
